import moment from "moment";

import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithRequest, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { CumulativePatientProfileModel } from "@/Patient/Api/Dto/Responses/CumulativePatientProfileModel";
import { InvalidateAndDeleteRvqImmunizationRequest } from "@/Patient/Components/MedicalItems/Immunization/RequestModels/InvalidateAndDeleteRvqImmunizationRequest";
import { MedicalItemsScreeningTestViewState } from "@/Patient/Components/MedicalItems/MedicalItemsScreeningTest";
import { EncounterSearchIndexRebuildMode, PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { EMRTemplateComments, EMRTemplateCPPVisibility, EMRTemplateExportVisibility } from "@/Patient/Models/MedicalSummary/EMRTemplateComments";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { FollowUpResponse } from "@/Patient/Models/MedicalSummary/Response/FollowUpResponse";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { MedicalItemViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";
import { MedicationNoteService } from "./MedicationNoteService";

class PatientMedicalSummaryResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_CPP_Load, this.LoadCumulativePatientProfileAndKeepDisplayedEncounters, SubServerOperations.MedicalNote.LoadSummaryAndKeepDisplayedEncounters);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_CPP_Load, this.LoadCumulativePatientProfileAndReloadDisplayedEncounters, SubServerOperations.MedicalNote.LoadSummaryAndReloadDisplayedEncounters);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_EMRTemplateComments_Get, this.LoadEMRTemplateComments);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_EMRTemplateComments_Save, this.SaveEMRTemplateComment);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_EMRTemplate_ToggleValidity, this.ToggleValidity);
        this.RegisterOperation(ServerOperations.Server.QuebecVaccinationRegistry_InvalidateAndDeleteImmunization, this.ToggleValidityForImmunization);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_EMRTemplate_ToggleCPPDisplay, this.ToggleVisibilityInCPP);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_SetMedicalSummaryVisibilityForExport, this.ToggleAlertVisibilityOnExport);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, this.PrintMedicalNote);

        this.RegisterOperation(ServerOperations.Server.Myle_LoadScreeningsAndFollowUps, this.updateFollowUpScreeningData);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalSummary_SaveObstetricalSummary, this.saveObstetricalSummary);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalSummary_LoadObstetricalSummary, this.loadObstetricalSummary);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_GetPendingItems, this.loadPatientPendingItems);

        this.RegisterOperation(ServerOperations.Server.Myle_GetEncounterFormsTitles, this.loadEncounterFormsTitles);
    }

    private PrintMedicalNote(response: ServerEvent): void {
        var fileName = response.Data.FileName;

        if (response.Data.IsSuccess) {
            PrintHelper.ShowPrintPreviewFileName(fileName);
        }
    }

    private LoadCumulativePatientProfileAndKeepDisplayedEncounters(response: ServerEventWithThen<any, any>, store: PatientStore): void {
        this.LoadCumulativePatientProfile(response, store);
    }

    private LoadCumulativePatientProfileAndReloadDisplayedEncounters(response: ServerEventWithThen<any, any>, store: PatientStore): void {
        this.LoadCumulativePatientProfile(response, store, true);
    }

    private LoadCumulativePatientProfile(response: ServerEventWithThen<any, any>, store: PatientStore, reloadDisplayedEncounters: boolean = false): void {
        // construct model and immutable model from response data
        let responseData: CumulativePatientProfileModel = response.Data;
        const patientModel = patientStore.PatientModels[response.PatientId];
        const patientId = responseData.PatientContactId;
        let summaryModel: IPatientMedicalSummaryModel = PatientMedicalSummaryHelper.getImmutableMedicalSummaryFromResponse(responseData as CumulativePatientProfileModel, patientModel);

        let filteredMedicationModels = patientStore.filterAndSortMedication(patientId, Immutable.fromJS(summaryModel.get("MedicalItemsMedicationAndPrescription")));
        summaryModel = summaryModel.set("MedicationNotes", new MedicationNoteService(
            responseData.MedicationNotes,
            summaryModel.get("MedicalItemsMedicationAndPrescription")
                .map(p => p.Model)
                .toArray()
        ));

        summaryModel = summaryModel.set("FilteredMedicalItemsMedicationAndPrescription", Immutable.List(filteredMedicationModels));

        const currentPrescriptions = store.PatientMedicalSummaryModels[responseData.PatientContactId]?.get("Prescriptions");
        const prescriptions = PatientMedicalSummaryHelper.CreatePrescriptionGroupsFromMedications(summaryModel.get("MedicalItemsMedicationAndPrescription"));

        const prescriptionsGeneralInstructions: Record<string, string> = responseData.PrescriptionsGeneralInstructions ?? {};
        summaryModel = summaryModel.set("PrescriptionsGeneralInstructions", prescriptionsGeneralInstructions);

        for (let prescription of prescriptions) {
            // LoadCumulativePatientProfileRequestHandler does not handle SignedPrintAuthorizations
            // this prevents the overwriting of the signed print authorizations which are fetched from GetSignedPrintAuthorizationRequestHandler
            const foundPrescription = currentPrescriptions?.find(p => p.PrescriptionId === prescription.PrescriptionId);

            if (foundPrescription) {
                prescription.SignedPrintAuthorizations = foundPrescription.SignedPrintAuthorizations;
            }

            prescription.GeneralInstruction = prescriptionsGeneralInstructions[prescription.PrescriptionId];
        }
        summaryModel = summaryModel.set("Prescriptions", Immutable.List(prescriptions));

        // ensure app is currently on PATIENTS/MEDICALSUMMARY before getting the alerts to display and triggering alert popup...
        let isMedicalSummaryTabActive = false;
        isMedicalSummaryTabActive = RouterHistory.location.pathname.toLocaleLowerCase().indexOf("patients") > -1;
        isMedicalSummaryTabActive = isMedicalSummaryTabActive && patientStore.PatientUIModels[responseData.PatientContactId] && patientStore.PatientUIModels[responseData.PatientContactId].get("ActiveVerticalTab") === PatientTab.MedicalSummary;

        const patientAlerts = summaryModel?.get("MedicalItemsAlertAndSpecialNeed");

        if (patientAlerts && isMedicalSummaryTabActive) {
            const alertsToDisplay = PatientMedicalSummaryHelper.getPatientAlertsToDisplay(patientAlerts.toArray(), patientModel);

            if (alertsToDisplay && alertsToDisplay.length > 0) {
                summaryModel = summaryModel.set("ShowAlertPopup", true);
                summaryModel = summaryModel.set("AlertsToDisplay", Immutable.List(alertsToDisplay));
            }
            else {
                summaryModel = summaryModel.set("ShowAlertPopup", false);
                sessionStore.hideGlobalOverlay();
            }
        }

        if (store.PatientMedicalSummaryModels[patientId]) {
            summaryModel = summaryModel.set("EncounterSearchTerm", store.PatientMedicalSummaryModels[patientId].get("EncounterSearchTerm"));
            summaryModel = summaryModel.set("EncounterSearchDateFrom", store.PatientMedicalSummaryModels[patientId].get("EncounterSearchDateFrom"));
            summaryModel = summaryModel.set("EncounterSearchDateTo", store.PatientMedicalSummaryModels[patientId].get("EncounterSearchDateTo"));
            summaryModel = summaryModel.set("IsDisplayingOnlyUnsigned", store.PatientMedicalSummaryModels[patientId].get("IsDisplayingOnlyUnsigned"));
            summaryModel = summaryModel.set("EncounterFilterMyNotes", store.PatientMedicalSummaryModels[patientId].get("EncounterFilterMyNotes"));
            summaryModel = summaryModel.set("IsDisplayOnlyClinicVisitNotes", store.PatientMedicalSummaryModels[patientId].get("IsDisplayOnlyClinicVisitNotes"));

            if (!reloadDisplayedEncounters)
                summaryModel = summaryModel.set("DisplayedEncounters", store.PatientMedicalSummaryModels[patientId].get("DisplayedEncounters"));
        }

        store.PatientMedicalSummaryModels[patientId] = summaryModel;
        if (reloadDisplayedEncounters && !IsNullOrWhiteSpace(summaryModel.get("EncounterSearchTerm")))
            sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged, { "value": summaryModel.get("EncounterSearchTerm") });

        store.IsLoading = false;
        store.ActionsMedicalSummary.LoadPatientPendingItems(patientId);

        response.then?.();
        window.setTimeout(() => {
            let op = new LocalEvent(LocalOperations.TabLoadEvents.MedicalSummaryLoaded);
            op.AddItem("id", response.PatientId);
            op.EmitChange = false;
            LocalEventDispatcher.dispatch(op);
        }, 100);
    }

    private LoadEMRTemplateComments(response: ServerEvent, store: PatientStore): void {
        var emrComments: EMRTemplateComments = response.Data;

        this.SetCommentsInModel(response.PatientId, emrComments, store);
    }

    private SaveEMRTemplateComment(response: ServerEvent, store: PatientStore): void {
        var emrComments: EMRTemplateComments = response.Data;

        this.SetCommentsInModel(response.PatientId, emrComments, store);
    }

    private ToggleValidity(response: ServerEvent, store: PatientStore): void {
        const emrComments: EMRTemplateComments = response.Data;
        this.SetCommentsInModel(response.PatientId, emrComments, store);
    }

    private ToggleValidityForImmunization(response: ServerEventWithRequest<InvalidateAndDeleteRvqImmunizationRequest>, store: PatientStore): void {
        const medicalImmunizationDeletionDate: Date = response.Data.MedicalImmunizationDeletionDate;
        const medicalItemId: string = response.requestDto.MedicalItemId;

        const emrComments: EMRTemplateComments = response.Data;
        this.SetCommentsInModel(response.PatientId, emrComments, store);

        if (response.Errors.any()) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        function updateImmunizationModel(immunizationList: Immutable.List<MedicalItem>): Immutable.List<MedicalItem> {
            const index = immunizationList.findIndex(x => x.Id === medicalItemId);
            if (index >= 0) {
                immunizationList = immunizationList
                    .set(index, new MedicalItemImmunization({
                        ...immunizationList.get(index),
                        QuebecRegistryDeletionDate: medicalImmunizationDeletionDate,
                        QuebecRegistryId: null
                    }));
            }
            return immunizationList;
        }

        let medicalItems = store.PatientMedicalItemsModels[response.PatientId];
        if (medicalItems)
            medicalItems = medicalItems.set("MedicalItemsList", updateImmunizationModel(medicalItems.get("MedicalItemsList")));

        let summaryModel = store.PatientMedicalSummaryModels[response.PatientId];
        if (summaryModel)
            summaryModel = summaryModel.set("MedicalItemsImmunization", updateImmunizationModel(summaryModel.get("MedicalItemsImmunization")) as Immutable.List<MedicalItemImmunization>);

        let noteModel = store.PatientMedicalNoteModels[response.PatientId];
        if (noteModel)
            noteModel = noteModel.set("MedicalItemsImmunization", updateImmunizationModel(noteModel.get("MedicalItemsImmunization")) as Immutable.List<MedicalItemImmunization>);

        store.PatientMedicalItemsModels[response.PatientId] = medicalItems;
        store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private ToggleVisibilityInCPP(response: ServerEvent, store: PatientStore): void {
        let visibility: EMRTemplateCPPVisibility = response.Data;
        let detailsModel = store.PatientMedicalItemsModels[response.PatientId];
        let summaryModel = store.PatientMedicalSummaryModels[response.PatientId];
        let noteModel = store.PatientMedicalNoteModels[response.PatientId];

        if (detailsModel && detailsModel.get("SelectedEMRTemplateId") == visibility.EMRTemplateId) {
            let allItems = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(detailsModel.get("AllMedicalItemsList").toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP);
            detailsModel = detailsModel.set("AllMedicalItemsList", Immutable.List(allItems));
            detailsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(detailsModel, null, detailsModel.get("NavigationOrigin"), summaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);

            store.PatientMedicalItemsModels[response.PatientId] = detailsModel;
        }
        if (noteModel) {
            let listOfProblemDiagnos = noteModel.get("MedicalItemsProblemsAndDiagnostics");
            if (listOfProblemDiagnos) {
                let updatedListOfProblemDiagnos = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfProblemDiagnos.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemProblemAndCondition[];
                noteModel = noteModel.set("MedicalItemsProblemsAndDiagnostics", Immutable.List(updatedListOfProblemDiagnos));
                store.PatientMedicalNoteModels[response.PatientId] = noteModel;
            }
        }
        if (summaryModel) {
            let listOfReaction = summaryModel.get("MedicalItemsMedicalReaction");
            if (listOfReaction) {
                let updatedListOfReaction = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfReaction.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemMedicalReaction[];
                summaryModel = summaryModel.set("MedicalItemsMedicalReaction", Immutable.List(updatedListOfReaction));
            }

            let listOfRisFactor = summaryModel.get("MedicalItemsRiskFactor");
            if (listOfRisFactor) {
                let updatedListOfRisFactor = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfRisFactor.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemRiskFactor[];
                summaryModel = summaryModel.set("MedicalItemsRiskFactor", Immutable.List(updatedListOfRisFactor));
            }

            let listOfAlert = summaryModel.get("MedicalItemsAlertAndSpecialNeed");
            if (listOfAlert) {
                let updatedListOfAlert = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfAlert.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP, visibility.IsVisibleOnExport) as MedicalItemAlertsAndSpecialNeeds[];
                summaryModel = summaryModel.set("MedicalItemsAlertAndSpecialNeed", Immutable.List(updatedListOfAlert));
            }

            let listOfFamilyHistory = summaryModel.get("MedicalItemsFamilyHistory");
            if (listOfFamilyHistory) {
                let updatedListOfFamilyHistory = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfFamilyHistory.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemFamilyHistory[];
                summaryModel = summaryModel.set("MedicalItemsFamilyHistory", Immutable.List(updatedListOfFamilyHistory));
            }

            let listOfImmunization = summaryModel.get("MedicalItemsImmunization");
            if (listOfImmunization) {
                let updatedListOfImmunization = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfImmunization.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemImmunization[];
                summaryModel = summaryModel.set("MedicalItemsImmunization", Immutable.List(updatedListOfImmunization));
            }

            let listOfVitalSigns = summaryModel.get("MedicalItemsVitalSign");
            if (listOfVitalSigns) {
                let updatedlistOfVitalSigns = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfVitalSigns.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemVitalSign[];
                summaryModel = summaryModel.set("MedicalItemsVitalSign", Immutable.List(updatedlistOfVitalSigns));
            }

            let listOfScreeningTests = summaryModel.get("MedicalItemsScreeningTest");
            if (listOfScreeningTests) {
                let updatedlistOfScreeningTests = PatientMedicalSummaryHelper.setEMRTemplateVisibilityInCpp(listOfScreeningTests.toArray(), visibility.EMRTemplateId, visibility.HideDate, visibility.IsDisplayedInCPP) as MedicalItemScreeningTestResponse[];
                summaryModel = summaryModel.set("MedicalItemsScreeningTest", Immutable.List(updatedlistOfScreeningTests));
            }

            store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
        }
    }

    private ToggleAlertVisibilityOnExport(response: ServerEvent, store: PatientStore): void {
        let visibility: EMRTemplateExportVisibility = response.Data;
        let summaryModel = store.PatientMedicalSummaryModels[response.PatientId];
        let detailsModel = store.PatientMedicalItemsModels[response.PatientId];
        const noteModel = store.PatientMedicalNoteModels[response.PatientId];

        if (summaryModel) {
            const listOfAlerts = summaryModel.get("MedicalItemsAlertAndSpecialNeed");
            const allItems = PatientMedicalSummaryHelper.setMedicalItemAlertsVisibilityOnExport(listOfAlerts.toArray(), visibility.EMRTemplateId, visibility.IsVisibleOnExport);
            summaryModel = summaryModel.set("MedicalItemsAlertAndSpecialNeed", Immutable.List(allItems));
            store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
        }

        if (detailsModel) {
            const allItems = PatientMedicalSummaryHelper.setEMRTemplateVisibilityOnExport(detailsModel.get("AllMedicalItemsList").toArray(), visibility.EMRTemplateId, visibility.IsVisibleOnExport);
            detailsModel = detailsModel.set("AllMedicalItemsList", Immutable.List(allItems));
            detailsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(detailsModel, null, detailsModel.get("NavigationOrigin"), summaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);

            store.PatientMedicalItemsModels[response.PatientId] = detailsModel;
        }
    }

    private SetCommentsInModel(patientId: string, emrComments: EMRTemplateComments, store: PatientStore): void {
        if (emrComments) {
            var detailsModel = store.PatientMedicalItemsModels[patientId];
            var summaryModel = store.PatientMedicalSummaryModels[patientId];

            if (detailsModel) {
                if (detailsModel.get("SelectedEMRTemplateId") && detailsModel.get("SelectedEMRTemplateId") == emrComments.EMRTemplateId) {
                    var allItems = detailsModel.get("AllMedicalItemsList").toArray();
                    this.updateMedicalItemValidity(allItems, emrComments);

                    this.updateMedicalItemValidityOfSummaryModel(summaryModel, emrComments);

                    var comments = detailsModel.get("EMRTemplateComments");
                    var summaryComments = summaryModel.get("EMRTemplateComments");

                    if (!(comments == null)) {
                        if (Object.keys(comments).indexOf(emrComments.EMRTemplateId) < 0) {
                            comments[emrComments.EMRTemplateId] = [];
                        }
                        comments[emrComments.EMRTemplateId] = emrComments.EMRTemplateComments;
                    }
                    if (!(summaryComments == null))
                        summaryComments[emrComments.EMRTemplateId] = emrComments.EMRTemplateComments;

                    var valNotes = detailsModel.get("EMRTemplateValidationNotes");
                    var summaryValNotes = summaryModel.get("EMRTemplateValidationNotes");
                    if (!(valNotes == null)) {
                        if (Object.keys(valNotes).indexOf(emrComments.EMRTemplateId) < 0) {
                            valNotes[emrComments.EMRTemplateId] = [];
                        }
                        valNotes[emrComments.EMRTemplateId] = emrComments.EMRTemplateValidationNotes;
                    }
                    if (!(summaryValNotes == null))
                        summaryValNotes[emrComments.EMRTemplateId] = emrComments.EMRTemplateValidationNotes;

                    detailsModel = detailsModel.set("EMRTemplateComments", comments);
                    detailsModel = detailsModel.set("EMRTemplateValidationNotes", valNotes);
                    detailsModel = detailsModel.set("AllMedicalItemsList", Immutable.List(allItems));

                    store.PatientMedicalItemsModels[patientId] = detailsModel;
                }
            }
        }
    }

    private updateMedicalItemValidity(items: MedicalItem[], emrComments: EMRTemplateComments): void {
        items = items.map(x => {
            if (emrComments.EMRTemplateIsInValid != null && emrComments.EMRTemplateIsInValid != undefined && x.EMRTemplateId == emrComments.EMRTemplateId) {
                x.EMRTemplateIsInvalid = emrComments.EMRTemplateIsInValid;
            }
            return x;
        });
    }

    private updateMedicalItemValidityOfSummaryModel(summaryModel: IPatientMedicalSummaryModel, emrComments: EMRTemplateComments): void {
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsActiveMedication").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsAlertAndSpecialNeed").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsFamilyHistory").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsGynecologicalHistory").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsImmunization").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsMedicalReaction").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsProblemsHistory").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsProblemsOnGoing").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsPsychiatricHistory").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsRiskFactor").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsSurgeryHistory").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsVitalSign").toArray(), emrComments);
        this.updateMedicalItemValidity(summaryModel.get("MedicalItemsScreeningTest").toArray(), emrComments);
    }

    private updateFollowUpScreeningData(response: ServerEvent, store: PatientStore): void {
        let patientContactId = response.Data.PatientContactId;

        this.updatePatientSummaryModel(store, patientContactId, response.Data.ScreeningTests, response.Data.FollowUps);
        this.updatePatientMedicalItemsModel(store, patientContactId, response.Data.ScreeningTests, response.Data.FollowUps);

        store.EmitChangeToListeners();
    }

    private updatePatientSummaryModel(store: PatientStore, patientContactId: string, screeningTests: MedicalItemScreeningTestResponse[], followUps: FollowUpResponse[]): void {
        let patientSummaryModel = store.PatientMedicalSummaryModels[patientContactId];
        if (!patientSummaryModel)
            return;

        if (screeningTests)
            patientSummaryModel = patientSummaryModel.set("MedicalItemsScreeningTest", Immutable.List(screeningTests.map(x => MyleHelper.CloneObject(x))));

        if (followUps)
            patientSummaryModel = patientSummaryModel.set("FollowUps", Immutable.List(followUps));

        store.PatientMedicalSummaryModels[patientContactId] = patientSummaryModel;
    }

    private updatePatientMedicalItemsModel(store: PatientStore, patientContactId: string, screeningTests: MedicalItemScreeningTestResponse[], followUps: FollowUpResponse[]): void {
        let patientMedicalItemsModel = store.PatientMedicalItemsModels[patientContactId];
        if (!patientMedicalItemsModel || !screeningTests)
            return;

        let selectedEMRTemplateId = patientMedicalItemsModel.get("SelectedEMRTemplateId");
        let screeningTestSameEMR = screeningTests.filter(x => x.EMRTemplateId === selectedEMRTemplateId);
        let uncommittedMedicalItem = patientMedicalItemsModel.get("MedicalItemsList").toArray().firstOrDefault(x => x.EMRTemplateId === selectedEMRTemplateId && !x.IsCommitted) as MedicalItemScreeningTestResponse;
        if (uncommittedMedicalItem && !isArrayNullOrUndefinedOrEmpty(screeningTestSameEMR)) {
            let uncommittedCreationDate = moment(uncommittedMedicalItem.CreationDate);

            const screeningWasDeletedDuringEdit = patientMedicalItemsModel.get("MedicalItemsList").size > screeningTestSameEMR.length + (uncommittedMedicalItem.IsNewMedicalItem ? 1 : 0);

            if (screeningWasDeletedDuringEdit) {
                patientMedicalItemsModel = patientMedicalItemsModel.set("ViewMode", MedicalItemViewMode.View);
                store.deleteMedicalItemCachedState(patientContactId, uncommittedMedicalItem.Id);
            }
            else {
                if (uncommittedMedicalItem.IsNewMedicalItem)
                    screeningTestSameEMR.push(uncommittedMedicalItem);

                screeningTestSameEMR.forEach(x => {
                    if (x.Id === uncommittedMedicalItem.Id) {
                        x.IsCommitted = false;
                        x.IsCollapsed = false;
                        return;
                    }

                    x.IsCollapsed = moment(x.CreationDate).isBefore(uncommittedCreationDate);
                    if (!x.IsCommitted) {
                        x.IsCommitted = true;
                        x.CommitDate = moment().toDate();
                    }
                });

                if (screeningTestSameEMR.length === patientMedicalItemsModel.get("MedicalItemsList").size) {
                    let medicalItemStateCache = patientStore.getMedicalItemCachedStateGeneric<MedicalItemsScreeningTestViewState>(patientContactId, uncommittedMedicalItem.Id);
                    if (medicalItemStateCache && medicalItemStateCache.followups && followUps) {
                        medicalItemStateCache.followups = medicalItemStateCache.followups.map(x => {
                            let updated = followUps.firstOrDefault(f => f.Id === x.Id && f.MedicalRequestType === x.MedicalRequestType);
                            if (!updated)
                                return null;

                            if (uncommittedMedicalItem.FollowUps && uncommittedMedicalItem.FollowUps.some(f => f.Id === x.Id))
                                return updated;

                            return (updated.LinkedEMRTemplateId || updated.IsInvalid) ? null : updated;
                        }).filter(x => x != null);
                    }
                }
            }
        }

        patientMedicalItemsModel = patientMedicalItemsModel
            .set("MedicalItemsList", Immutable.List(screeningTestSameEMR))
            .set("AllMedicalItemsList", Immutable.List(screeningTests.map(x => MyleHelper.CloneObject(x))));

        store.PatientMedicalItemsModels[patientContactId] = patientMedicalItemsModel;
    }

    private saveObstetricalSummary(response: ServerEvent, store: PatientStore): void {
        store.ActionsMedicalSummary.loadObstetricalSummary(store.getCurrentPatient().PatientResponse.ContactFacet_id);
    }

    private loadObstetricalSummary(response: ServerEvent, store: PatientStore): void {
        const patientContactId = store.getCurrentPatientContactId();
        let patientMedicalSummary = store.PatientMedicalSummaryModels[patientContactId];

        patientMedicalSummary = patientMedicalSummary.set("ObstetricalSummary", response.Data.ObstetricalSummary);

        store.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummary;
    }

    private loadPatientPendingItems(response: ServerEvent, store: PatientStore): void {
        const patientContactId = response.Data.PatientContactId;
        let patientMedicalSummary = store.PatientMedicalSummaryModels[patientContactId];

        patientMedicalSummary = patientMedicalSummary
            .set("Requests", Immutable.List(response.Data.Requests))
            .set("ConsultationRequests", Immutable.List(response.Data.ConsultationRequests));

        store.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummary;
        store.IsLoading = false;
    }

    private loadEncounterFormsTitles(response: ServerEvent, store: PatientStore): void {
        const patientContactId = response.PatientId;
        const responseData: { EncounterFormsTitles: Record<string, Set<string>> } = response.Data;
        let patientMedicalSummary = store.PatientMedicalSummaryModels[patientContactId];

        patientMedicalSummary = patientMedicalSummary.set("EncounterFormsTitles", responseData.EncounterFormsTitles);
        patientMedicalSummary = PatientMedicalSummaryHelper.FilterEncountersClientSide(patientMedicalSummary, EncounterSearchIndexRebuildMode.ForceRebuild);

        store.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummary;
    }
}

export { PatientMedicalSummaryResponseHandler };
